import { useEffect, useState, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Header } from "./Header";
import { Toaster } from "@/components/ui/Toaster";
import "./style.css";
import { IMAGES } from "@/assets/IMAGES";
import { Tooltip, TooltipContent, TooltipTrigger } from "../MobileToolTip";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { extractLinks } from "@/utils/Helper";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { ResourceTypes } from "@/app/types/roles";
import { useDispatch } from "react-redux";
import { setSidePanelDispatch } from "@/features/SidePanelSlice";

const DashboardLayout: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sidePanel, setSidePanel] = useState<ResourceTypes[]>([]);
  const [backtogym, setBacktogym] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(true);

  // Fetch and set sidePanel from localStorage
  useEffect(() => {
    const updateSidePanel = () => {
      const sidepanel = localStorage.getItem("sidePanel");
      if (sidepanel) {
        try {
          const decodedSidepanel = JSON.parse(atob(sidepanel));
          const links = extractLinks(decodedSidepanel);
          setSidePanel(decodedSidepanel);
          setBacktogym(links[0]);
        } catch (error) {
          console.error("Error decoding Base64 string sidepanel:", error);
        }
      }
    };

    updateSidePanel();

    const handleUserSwitch = () => {
      updateSidePanel();
    };

    window.addEventListener('userSwitched', handleUserSwitch);

    return () => {
      window.removeEventListener('userSwitched', handleUserSwitch);
    };
  }, []);

  // --- Scoped sidebar detection ---
  const scopedItem = useMemo<ResourceTypes | null>(() => {
    if (!sidePanel.length) return null;
    for (const item of sidePanel) {
      if (
        item.is_scoped &&
        pathname.startsWith(item.link + "/") &&
        pathname !== item.link
      ) {
        return item;
      }
    }
    return null;
  }, [sidePanel, pathname]);

  const scopedBasePath = useMemo<string | null>(() => {
    if (!scopedItem) return null;
    const scopedSegments = scopedItem.link.split("/").filter(Boolean);
    const currentSegments = pathname.split("/").filter(Boolean);
    const idSegment = currentSegments[scopedSegments.length];
    return idSegment ? `${scopedItem.link}/${idSegment}` : scopedItem.link;
  }, [scopedItem, pathname]);

  const resolveScopedLink = (link: string) => {
    if (!scopedBasePath) return link;
    if (link.startsWith("/")) return link;
    return `${scopedBasePath}/${link}`;
  };

  const isActiveScopedChild = (link: string) => {
    const resolved = resolveScopedLink(link);
    if (pathname === resolved) return true;
    if (pathname.startsWith(resolved)) {
      const remaining = pathname.slice(resolved.length);
      return remaining === "" || remaining.startsWith("/");
    }
    return false;
  };
  // --- end scoped detection ---

  // Resolve a sidebar item link — if it's relative and we're in a scoped context,
  // prefix it with the scoped basePath (e.g. "tickets" → "/admin/events/123/tickets")
  const resolveLink = (link: string) => {
    if (scopedBasePath && !link.startsWith("/")) {
      return `${scopedBasePath}/${link}`;
    }
    return link;
  };

  // Determine if the link is active
  const isActiveLink = (targetPath: string) => {
    const resolved = resolveLink(targetPath);
    if (pathname === resolved) return true;
    if (pathname.startsWith(resolved)) {
      const remainingPath = pathname.slice(resolved.length);
      return remainingPath === "" || remainingPath.startsWith("/");
    }
    return false;
  };

  // Toggle sidebar visibility
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
    dispatch(setSidePanelDispatch(!isSidebarOpen))
  };

  // Items to render in the sidebar — scoped children or the full sidePanel
  const sidebarItems = scopedItem?.children ?? sidePanel;

  return (
    <div className="font-poppins flex h-full w-full relative">
      <nav
        className={`relative border-r text-black shadow-md h-screen custom-scrollbar-right transition-all duration-300 ${isSidebarOpen ? "w-[275px]" : "w-[72px]"
          }`}
      >
        <div
          style={{ direction: "ltr" }}
          className={` flex h-16 items-center ${!isSidebarOpen ? "justify-center" : "justify-between"} px-4 border-gradient sticky top-0 z-30 bg-white`}
        >
          {scopedItem ? (
            <div className="flex items-center gap-2 font-semibold">
              <img
                src={IMAGES.logo_icon}
                className={`${isSidebarOpen ? "h-[3rem] w-[3rem]" : "h-[3rem] w-[3rem]"}"`}
                alt="Dashboard"
              />
              {isSidebarOpen && (
                <div className="flex flex-col">
                  <span className="text-[0.65rem] text-gray-500 leading-tight">{scopedItem.name}</span>
                  <span className="text-sm font-semibold text-primary leading-tight capitalize">
                    {scopedItem.name}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <Link
              to={sidePanel[0]?.link}
              className="flex items-center gap-2 font-semibold "
            >
              <img
                src={IMAGES.logo_icon}
                className={`${isSidebarOpen ? "h-[3rem] w-[3rem]" : "h-[3rem] w-[3rem]"}"`}
                alt="Dashboard"
              />
              <span
                className={`${!isSidebarOpen && "hidden"
                  } text-[1.3rem] text-center font-semibold text-primary`}
              >
                QMS{" "}
              </span>
            </Link>
          )}

          <button
            onClick={toggleSidebar}
            className={`absolute  top-[3.2rem] -right-1.5  z-50 size-6 flex items-center rounded-md bg-white transition  border-[1px] border-gradient p-0 `}
          >
            {isSidebarOpen ? (
              <ChevronLeft className=" text-gray-700 font-bold pr-1" />
            ) : (
              <ChevronRight className=" text-gray-700 font-bold pr-1" />
            )}
          </button>
        </div>
        {/* Dashboard / back item in scoped context */}
        {scopedItem && (
          <div
            style={{ direction: "ltr" }}
            className="px-2 pt-2"
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => {
                    navigate(scopedItem.link);
                  }}
                  className={`group mb-1 flex items-center gap-2 rounded-md p-1 w-full transition-colors text-sm hover:bg-secondary ${isSidebarOpen ? "justify-start" : "justify-center"}`}
                >
                  <div className="w-7 h-7 bg-secondary rounded-lg justify-center flex items-center">
                    <i className={`${scopedItem.icon || "fa fa-chart-line"} text-primary stroke-current`}></i>
                  </div>
                  <span className={`${!isSidebarOpen && "hidden"} text-gray-500 group-hover:text-primary`}>
                    Dashboard
                  </span>
                </button>
              </TooltipTrigger>
              {!isSidebarOpen && (
                <TooltipContent
                  className="bg-secondary text-primary border-none"
                  side="right"
                >
                  <p>Dashboard</p>
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        )}

        <div
          style={{ direction: "ltr" }}
          className="flex flex-col gap-2 px-2 py-2"
        >
          {sidebarItems?.map((item: any, i: number) => (
            <div
              key={Number(i)}
              className={`${isSidebarOpen ? "" : "justify-center items-center flex"}`}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  {(item.children?.length === 0 || item.is_scoped || scopedItem) && item.link !== "/" && (
                    <Link
                      to={scopedItem ? resolveScopedLink(item.link) : resolveLink(item.link)}
                      className={`group mb-1 flex items-center gap-2 rounded-md p-1 transition-colors ${isSidebarOpen
                        ? "justify-start text-sm"
                        : "justify-center text-lg"
                        } ${scopedItem
                          ? isActiveScopedChild(item.link)
                            ? "bg-primary"
                            : "hover:bg-secondary"
                          : isActiveLink(item.link)
                            ? "bg-primary"
                            : "hover:bg-secondary"
                        }`}
                    >
                      <div
                        className={` w-7 h-7 ${scopedItem
                          ? isActiveScopedChild(item.link)
                            ? "bg-primary"
                            : "bg-secondary "
                          : isActiveLink(item.link)
                            ? "bg-primary"
                            : "bg-secondary "
                          } rounded-lg justify-center flex items-center`}
                      >
                        <i
                          className={`${item.icon}  ${scopedItem
                            ? isActiveScopedChild(item.link)
                              ? "text-secondary"
                              : "text-primary stroke-current"
                            : isActiveLink(item.link)
                              ? "text-secondary"
                              : "text-primary stroke-current"
                            }`}
                        ></i>
                      </div>
                      <span
                        className={`${!isSidebarOpen && "hidden"} ${scopedItem
                          ? isActiveScopedChild(item.link)
                            ? "text-secondary"
                            : "text-gray-500 group-hover:text-primary"
                          : isActiveLink(item.link)
                            ? "text-secondary"
                            : "text-gray-500 group-hover:text-primary"
                          }`}
                      >
                        {item.name}
                      </span>
                    </Link>
                  )}
                </TooltipTrigger>
                {!isSidebarOpen && (
                  <TooltipContent
                    className="bg-secondary text-primary border-none"
                    side="right"
                  >
                    <p>{item.name}</p>
                  </TooltipContent>
                )}
              </Tooltip>

              {!scopedItem && !item.is_scoped && item.children &&
                item.children?.length > 0 &&
                (isSidebarOpen ? (
                  <Accordion type="single" collapsible >
                    <AccordionItem value="item-1 !border-none" id="accordion">
                      <AccordionTrigger
                        className={`group flex items-center gap-2 rounded-md p-1 transition-colors !no-underline ${isSidebarOpen ? "justify-between text-sm" : "justify-center text-lg "} `}
                      >
                        <div className="text-gray-500 flex items-center gap-2">
                          <div
                            className={` w-7 h-7 ${isActiveLink(item.link) ? "bg-primary" : "bg-secondary "} rounded-lg justify-center flex items-center`}
                          >
                            <i
                              className={`${item.icon}  ${isActiveLink(item.link) ? "text-black" : "text-primary stroke-current"}`}
                            ></i>
                          </div>
                          <span
                            className={`${!isSidebarOpen && "hidden"} font-normal`}
                          >
                            {item.name}
                          </span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="ml-7 flex flex-col  gap-1 mt-2 [data-state=closed]:animate-accordion-up [data-state=open]:animate-accordion-down ">
                          {item.children?.map((child: any, index: number) => (
                            <Link
                              key={Number(index)}
                              to={child.link}
                              className={`justify-start rounded-md px-3  py-1 ${isActiveLink(child.link) ? "bg-primary text-secondary" : "hover:bg-muted"} ${!isSidebarOpen && "hidden"}`}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                ) : (
                  <Popover>
                    <PopoverTrigger className="group flex items-center gap-2 rounded-md p-1 transition-colors justify-center">
                      <div
                        className={`w-7 h-7 ${isActiveLink(item.link) ? "bg-primary" : "bg-secondary"} rounded-lg flex items-center justify-center`}
                      >
                        <i
                          className={`${item.icon} ${isActiveLink(item.link) ? "text-black" : "text-primary"}`}
                        ></i>
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="bg-white text-primary border-none">
                      <div className="flex flex-col gap-1">
                        {item.children.map((child: any, index: number) => (
                          <Link
                            key={Number(index)}
                            to={child.link}
                            className={`justify-start rounded-md px-3 py-1 ${isActiveLink(child.link) ? "bg-secondary text-primary" : "hover:bg-muted"}`}
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                ))}
            </div>
          ))}
        </div>
      </nav>
      <div className="flex-1 h-screen custom-scrollbar  w-[calc(100%-275px)]">
        <Header />
        <main className="bg-outletcolor min-h-[calc(100vh-64px)]">
          <div className="w-full mx-auto max-w-[2000px]">
            <Outlet />
            <Toaster />
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;